import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, CircularProgress,
  Alert, Stack, Divider,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckIcon from '@mui/icons-material/Check';
import PlaceIcon from '@mui/icons-material/Place';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { tasksAPI } from '../../services/api';
import { STATUS_LABELS, STATUS_COLORS } from './taskConstants';

const GROUPS = [
  { key: 'today', label: 'Hoy' },
  { key: 'week', label: 'Esta semana' },
  { key: 'later', label: 'Proximas' },
  { key: 'done', label: 'Terminadas' },
];

/**
 * Lista de tareas del tecnico. La accion principal esta en la propia tarjeta,
 * de modo que empezar o terminar una tarea es UN solo toque, sin abrirla.
 */
export default function MyTasks({ onOpenTask }) {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await tasksAPI.my();
      setGroups(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se han podido cargar las tareas');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Accion principal segun el estado: un toque desde la tarjeta
  const primaryAction = (task) => {
    if (task.status === 'in_progress') {
      return { label: 'Finalizar', icon: <CheckIcon />, color: 'success', next: 'done' };
    }
    if (['pending', 'assigned', 'blocked'].includes(task.status)) {
      return { label: 'Empezar', icon: <PlayArrowIcon />, color: 'primary', next: 'in_progress' };
    }
    return null;
  };

  const runAction = async (task, action, e) => {
    e.stopPropagation();
    // Finalizar puede requerir horas o foto: se resuelve en el detalle
    if (action.next === 'done' && task.requires_photo) {
      onOpenTask(task.id);
      return;
    }
    setBusyId(task.id);
    try {
      await tasksAPI.setStatus(task.id, { status: action.next });
      await load();
    } catch (err) {
      // Si el backend pide algo mas (foto, motivo), se abre el detalle
      onOpenTask(task.id);
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!groups) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  const total = GROUPS.reduce((n, g) => n + (groups[g.key]?.length || 0), 0);
  if (total === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No tienes tareas asignadas.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5, pb: 10 }}>
      {GROUPS.map((group) => {
        const tasks = groups[group.key] || [];
        if (!tasks.length) return null;
        return (
          <Box key={group.key} sx={{ mb: 2.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ pl: 0.5 }}>
              {group.label} ({tasks.length})
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              {tasks.map((task) => {
                const action = primaryAction(task);
                return (
                  <Card key={task.id} elevation={2}
                    onClick={() => onOpenTask(task.id)}
                    sx={{ cursor: 'pointer',
                          borderLeft: task.is_overdue ? '5px solid' : '5px solid transparent',
                          borderLeftColor: task.is_overdue ? 'error.main' : 'transparent' }}>
                    <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                          {task.title}
                        </Typography>
                        <Chip size="small" label={STATUS_LABELS[task.status] || task.status}
                          color={STATUS_COLORS[task.status] || 'default'} />
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {task.manufacturing_order_code || 'Sin orden'}
                        {task.project_name ? ` · ${task.project_name}` : ''}
                      </Typography>

                      {task.location && (
                        <Typography variant="body2" color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <PlaceIcon fontSize="inherit" /> {task.location}
                        </Typography>
                      )}

                      {/* Aviso de que la tarea trae instrucciones: se leen al abrirla */}
                      {task.safety_notes && (
                        <Typography variant="body2" color="warning.main"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <WarningAmberIcon fontSize="inherit" /> Lleva medidas de seguridad
                        </Typography>
                      )}
                      {task.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}
                          style={{
                            display: '-webkit-box', WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical', overflow: 'hidden',
                          }}>
                          {task.description}
                        </Typography>
                      )}

                      {task.status === 'blocked' && task.blocked_reason && (
                        <Typography variant="body2" color="error"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <BlockIcon fontSize="inherit" /> {task.blocked_reason}
                        </Typography>
                      )}

                      {action && (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          <Button
                            fullWidth variant="contained" color={action.color}
                            startIcon={action.icon} size="large"
                            disabled={busyId === task.id}
                            onClick={(e) => runAction(task, action, e)}
                            sx={{ height: 48, fontSize: 16 }}
                          >
                            {action.label}
                          </Button>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}
